import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ComputerIcon from '@mui/icons-material/Computer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WebhookIcon from '@mui/icons-material/Webhook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ErrorIcon from '@mui/icons-material/Error';
import {
  getCallerTypeDisplayInfo,
  getValidationStatusDisplayInfo,
} from '../../../api/conceptgate';
import './ValidationCallHistoryModal.css';

const CallerTypeIcon = ({ callerType }) => {
  switch (callerType) {
    case 'UI':
      return <ComputerIcon fontSize="small" />;
    case 'MCP':
      return <SmartToyIcon fontSize="small" />;
    case 'WEBHOOK':
      return <WebhookIcon fontSize="small" />;
    default:
      return null;
  }
};

const RuleStatusIcon = ({ status }) => {
  switch (status) {
    case 'PASSED':
      return <CheckCircleIcon fontSize="small" className="rule-status-icon passed" />;
    case 'FAILED':
      return <CancelIcon fontSize="small" className="rule-status-icon failed" />;
    case 'SKIPPED':
      return <SkipNextIcon fontSize="small" className="rule-status-icon skipped" />;
    case 'ERROR':
      return <ErrorIcon fontSize="small" className="rule-status-icon error" />;
    default:
      return null;
  }
};

const ValidationCallHistoryModal = ({ entry, details, onClose }) => {
  if (!entry) return null;

  const callerInfo = getCallerTypeDisplayInfo(entry.callerType);
  const statusInfo = getValidationStatusDisplayInfo(entry.responseStatus);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  const formatJson = (jsonString) => {
    if (!jsonString) return null;
    try {
      const obj = JSON.parse(jsonString);
      return JSON.stringify(obj, null, 2);
    } catch {
      return jsonString;
    }
  };

  const parseResponsePayload = (payload) => {
    if (!payload) return null;
    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  };

  const responseData = parseResponsePayload(details?.responsePayload);
  const rules = responseData?.rules || [];

  return (
    <div className="vch-modal-overlay" onClick={onClose}>
      <div className="vch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vch-modal-header">
          <div className="vch-modal-title-row">
            <div className="vch-modal-caller" style={{ color: callerInfo.color }}>
              <CallerTypeIcon callerType={entry.callerType} />
              <span>{callerInfo.label}</span>
            </div>
            <div className="vch-modal-instrument">
              {entry.instrument || '-'}
              {entry.direction && <span className="vch-modal-direction">{entry.direction}</span>}
            </div>
            <div
              className="vch-modal-status"
              style={{ backgroundColor: statusInfo?.color || '#95a5a6' }}
            >
              {entry.responseStatus || 'UNKNOWN'}
            </div>
          </div>
          <button className="vch-modal-close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="vch-modal-content">
          {details?.errorMessage && (
            <div className="vch-modal-section error">
              <div className="vch-modal-section-title">Error</div>
              <div className="vch-modal-error-message">{details.errorMessage}</div>
            </div>
          )}

          {/* Pipeline Info for Webhooks */}
          {entry.callerType === 'WEBHOOK' && (
            <div className="vch-modal-section">
              <div className="vch-modal-section-title">Pipeline</div>
              <div className="vch-modal-pipeline-info">
                <span className="vch-modal-pipeline-name">{entry.pipelineName || 'Unknown Pipeline'}</span>
                {entry.pipelineId && (
                  <span className="vch-modal-pipeline-id">{entry.pipelineId}</span>
                )}
              </div>
            </div>
          )}

          {/* Rule Results */}
          {rules.length > 0 && (
            <div className="vch-modal-section">
              <div className="vch-modal-section-title">
                Validation Rules ({responseData?.rulesPassed || 0} passed, {responseData?.rulesFailed || 0} failed, {responseData?.rulesSkipped || 0} skipped)
              </div>
              <div className="vch-modal-rules-list">
                {rules.map((rule, index) => (
                  <div key={index} className={`vch-modal-rule ${rule.status?.toLowerCase()}`}>
                    <div className="vch-modal-rule-header">
                      <RuleStatusIcon status={rule.status} />
                      <span className="vch-modal-rule-name">{rule.ruleName}</span>
                      {rule.durationMs && (
                        <span className="vch-modal-rule-duration">{rule.durationMs}ms</span>
                      )}
                    </div>
                    {rule.message && (
                      <div className="vch-modal-rule-message">{rule.message}</div>
                    )}
                    {rule.suggestedFix && (
                      <div className="vch-modal-rule-fix">
                        <strong>Fix:</strong> {rule.suggestedFix}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary Stats */}
          {responseData && !rules.length && (
            <div className="vch-modal-section">
              <div className="vch-modal-section-title">Validation Summary</div>
              <div className="vch-modal-stats-grid">
                <div className="vch-modal-stat">
                  <span className="vch-modal-stat-value passed">{responseData.rulesPassed || 0}</span>
                  <span className="vch-modal-stat-label">Passed</span>
                </div>
                <div className="vch-modal-stat">
                  <span className="vch-modal-stat-value failed">{responseData.rulesFailed || 0}</span>
                  <span className="vch-modal-stat-label">Failed</span>
                </div>
                <div className="vch-modal-stat">
                  <span className="vch-modal-stat-value skipped">{responseData.rulesSkipped || 0}</span>
                  <span className="vch-modal-stat-label">Skipped</span>
                </div>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="vch-modal-section">
            <div className="vch-modal-section-title">Details</div>
            <div className="vch-modal-metadata-grid">
              <div className="vch-modal-metadata-item">
                <span className="vch-modal-metadata-label">Time</span>
                <span className="vch-modal-metadata-value">{formatTimestamp(entry.createdAt)}</span>
              </div>
              <div className="vch-modal-metadata-item">
                <span className="vch-modal-metadata-label">Duration</span>
                <span className="vch-modal-metadata-value">{entry.durationMs ? `${entry.durationMs}ms` : '-'}</span>
              </div>
              {details?.ipAddress && (
                <div className="vch-modal-metadata-item">
                  <span className="vch-modal-metadata-label">IP Address</span>
                  <span className="vch-modal-metadata-value">{details.ipAddress}</span>
                </div>
              )}
              {details?.validationId && (
                <div className="vch-modal-metadata-item full-width">
                  <span className="vch-modal-metadata-label">Validation ID</span>
                  <span className="vch-modal-metadata-value mono">{details.validationId}</span>
                </div>
              )}
            </div>
          </div>

          {/* Request Payload */}
          {details?.requestPayload && (
            <div className="vch-modal-section">
              <div className="vch-modal-section-title">Request Payload</div>
              <pre className="vch-modal-code">{formatJson(details.requestPayload)}</pre>
            </div>
          )}

          {/* User Agent */}
          {details?.userAgent && (
            <div className="vch-modal-section">
              <div className="vch-modal-section-title">User Agent</div>
              <div className="vch-modal-user-agent">{details.userAgent}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidationCallHistoryModal;
